/**
 * usePremiumSale - which sale UI to show right now ("active" | "teaser" | "none").
 *
 * Uses the browser clock for DISPLAY only; checkout price is decided on the server.
 * Admins can preview with ?previewSale=1 (sale) or ?previewSale=teaser, and stop with
 * ?previewSale=0. The preview is remembered for this browser tab (sessionStorage).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { salePhaseAt } from "../config/premiumSale";

const KEY = "scope180-previewSale";

function readPreview(search) {
  try {
    const v = new URLSearchParams(search).get("previewSale");
    if (v === "0" || v === "off") sessionStorage.removeItem(KEY);
    else if (v === "1" || v === "sale" || v === "active") sessionStorage.setItem(KEY, "active");
    else if (v === "teaser") sessionStorage.setItem(KEY, "teaser");
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export default function usePremiumSale({ tick = false } = {}) {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [now, setNow] = useState(() => Date.now());
  const [stored, setStored] = useState(() => readPreview(location.search));

  useEffect(() => {
    setStored(readPreview(location.search));
  }, [location.search]);

  const realPhase = salePhaseAt(now);
  const previewPhase = isAdmin && stored && realPhase === "none" ? stored : null;
  const phase = previewPhase || realPhase;

  // Re-check the clock: every second for a visible countdown, else every minute
  // (so the banner appears / disappears on its own at the start and end).
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tick && phase === "active" ? 1000 : 60000);
    return () => clearInterval(id);
  }, [tick, phase]);

  return { phase, isPreview: !!previewPhase, now };
}
